// Keep the addition separate from cart order: adding an existing line need not move it.
let addedCartVariantIds = [];
const cartUpsellSelections = new Map();
const upsellIgnoredWords = new Set(["de", "du", "des", "le", "la", "les", "un", "une", "et", "ou", "au", "aux", "en", "pour", "avec", "sans", "sur", "the", "and", "for", "with", "from", "all", "our", "in", "of", "to", "der", "die", "das", "und", "mit", "fur", "aus", "von", "den", "dem", "ein", "eine", "del", "los", "las", "una", "con", "por", "para", "sin", "dei", "degli", "delle", "gli", "per", "tra", "fra"]);

document.addEventListener("variant:add", (event) => {
  addedCartVariantIds = (event.detail?.items || []).map((item) => String(item.variant_id || item.id));
});

function upsellWords(value) {
  const words = String(value).normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().match(/[a-z0-9]+/g) || [];
  const normalized = words
    .filter((word) => word.length > 2 && !upsellIgnoredWords.has(word))
    .map((word) => word.replace(/s$/, ""));
  if (normalized.includes("bangle") && !normalized.includes("bracelet")) {
    normalized.push("bracelet");
  }
  return normalized;
}

function matchingUpsellTab(tabs, labels) {
  const productWords = new Set(labels.flatMap(upsellWords));
  let bestTab = null;
  let bestScore = 0;

  tabs.forEach((tab) => {
    const categoryWords = new Set(upsellWords(tab.textContent));
    const score = Array.from(categoryWords).filter((word) => productWords.has(word)).length;
    if (score > bestScore) {
      bestTab = tab;
      bestScore = score;
    }
  });

  return bestTab;
}

class CartUpsellTabs extends HTMLElement {
  connectedCallback() {
    this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));

    this.tabs.forEach((tab, index) => {
      tab.addEventListener("click", () => this.selectTab(tab));
      tab.addEventListener("keydown", (event) => this.handleKeydown(event, index));
    });

    this.querySelectorAll("[data-upsell-scroll]").forEach((button) => {
      button.addEventListener("click", () => this.scrollProducts(button));
    });

    this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));
    this.resizeObserver = window.ResizeObserver
      ? new ResizeObserver(() => this.panels.forEach((panel) => this.updateArrows(panel)))
      : null;

    this.panels.forEach((panel) => {
      const scroller = panel.querySelector(".cart-upsell-tabs__products");
      scroller?.addEventListener("scroll", () => this.updateArrows(panel), { passive: true });
      if (scroller) this.resizeObserver?.observe(scroller);
    });

    this.restoreSelection();
    requestAnimationFrame(() => this.panels.forEach((panel) => this.updateArrows(panel)));
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
  }

  restoreSelection() {
    const previous = cartUpsellSelections.get(this.dataset.sectionId);
    let selectedTab = this.tabs.find((tab) => tab.id === previous?.tabId) || this.tabs[0];

    // Liquid supplies category metadata missing from AJAX line items. Prefer the
    // explicit jewelry type, then fall back to the product's collection names.
    if (previous?.addition !== addedCartVariantIds) {
      const cartItems = JSON.parse(this.dataset.cartItems || "[]");
      for (const variantId of addedCartVariantIds) {
        const item = cartItems.find((cartItem) => String(cartItem.variantId) === variantId);
        const categoryLabels = item?.jewelryTypes || [];
        const collectionHandles = item?.collectionHandles || [];
        const matchedTab = item && (
          categoryLabels.map((label) => matchingUpsellTab(this.tabs, [label])).find(Boolean) ||
          this.tabs.find((tab) => tab.dataset.collectionHandle && collectionHandles.includes(tab.dataset.collectionHandle)) ||
          matchingUpsellTab(this.tabs, categoryLabels) ||
          matchingUpsellTab(this.tabs, item.collections || [])
        );
        if (matchedTab) {
          selectedTab = matchedTab;
          break;
        }
      }
    }

    if (selectedTab) this.selectTab(selectedTab, false);
  }

  selectTab(selectedTab, scrollIntoView = true) {
    cartUpsellSelections.set(this.dataset.sectionId, { tabId: selectedTab.id, addition: addedCartVariantIds });
    this.tabs.forEach((tab) => {
      const isSelected = tab === selectedTab;
      const panel = this.querySelector(`#${CSS.escape(tab.getAttribute("aria-controls"))}`);

      tab.setAttribute("aria-selected", isSelected.toString());
      tab.tabIndex = isSelected ? 0 : -1;

      if (panel) {
        panel.hidden = !isSelected;
        panel.querySelectorAll("a, button, input").forEach((element) => {
          element.tabIndex = isSelected ? 0 : -1;
        });
      }
    });

    const selectedPanel = this.querySelector(`#${CSS.escape(selectedTab.getAttribute("aria-controls"))}`);
    requestAnimationFrame(() => this.updateArrows(selectedPanel));
    if (scrollIntoView) selectedTab.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
  }

  handleKeydown(event, currentIndex) {
    if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;

    event.preventDefault();

    let nextIndex = currentIndex;

    if (event.key === "ArrowRight") nextIndex = (currentIndex + 1) % this.tabs.length;
    if (event.key === "ArrowLeft") nextIndex = (currentIndex - 1 + this.tabs.length) % this.tabs.length;
    if (event.key === "Home") nextIndex = 0;
    if (event.key === "End") nextIndex = this.tabs.length - 1;

    this.selectTab(this.tabs[nextIndex]);
    this.tabs[nextIndex].focus();
  }

  scrollProducts(button) {
    const panel = button.closest('[role="tabpanel"]');
    const scroller = panel?.querySelector(".cart-upsell-tabs__products");
    const card = scroller?.querySelector(".cart-upsell-card");

    if (!scroller || !card) return;

    const gap = parseFloat(window.getComputedStyle(scroller).columnGap) || 0;
    const direction = button.dataset.upsellScroll === "next" ? 1 : -1;
    const directionAwareMultiplier = document.documentElement.dir === "rtl" ? -1 : 1;

    scroller.scrollBy({
      left: direction * directionAwareMultiplier * (card.offsetWidth + gap),
      behavior: "smooth",
    });
  }

  updateArrows(panel) {
    const scroller = panel?.querySelector(".cart-upsell-tabs__products");
    const previousButton = panel?.querySelector('[data-upsell-scroll="previous"]');
    const nextButton = panel?.querySelector('[data-upsell-scroll="next"]');

    if (!scroller || !previousButton || !nextButton) return;

    const maximumScroll = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
    const scrollPosition = document.documentElement.dir === "rtl"
      ? Math.abs(scroller.scrollLeft)
      : scroller.scrollLeft;

    previousButton.disabled = scrollPosition <= 1;
    nextButton.disabled = scrollPosition >= maximumScroll - 1;
  }
}

if (!window.customElements.get("cart-upsell-tabs")) {
  window.customElements.define("cart-upsell-tabs", CartUpsellTabs);
}

class CartGiftWrap extends HTMLElement {
  connectedCallback() {
    this.checkbox = this.querySelector('input[type="checkbox"]');
    this.noteInput = this.querySelector('input[name="note"]');
    this.checkbox?.addEventListener("change", () => this.updateCartNote());
  }

  async updateCartNote() {
    const message = this.dataset.message?.trim();

    if (!message || !this.checkbox || !this.noteInput) return;

    const previousNote = this.noteInput.value;
    const noteLines = previousNote
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean)
      .filter((line) => line !== message);

    if (this.checkbox.checked) noteLines.push(message);

    const updatedNote = noteLines.join("\n");
    this.noteInput.value = updatedNote;
    this.checkbox.disabled = true;

    try {
      const response = await fetch(`${Shopify.routes.root}cart/update.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ note: updatedNote }),
      });

      if (!response.ok) throw new Error("Unable to update the gift-wrap note");
    } catch (error) {
      this.checkbox.checked = !this.checkbox.checked;
      this.noteInput.value = previousNote;
      console.error(error);
    } finally {
      this.checkbox.disabled = false;
    }
  }
}

if (!window.customElements.get("cart-gift-wrap")) {
  window.customElements.define("cart-gift-wrap", CartGiftWrap);
}
